import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, ExternalLink, Calendar, FileText, Shield, DollarSign, Languages, Sparkles } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { CountryGuidesLineNote } from "@/components/CountryGuidesLineNote";


function fmtUsd(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n);
}


function Section({ icon: Icon, label, title, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="mt-12 grid md:grid-cols-3 gap-6 md:gap-10"
    >
      <div className="md:col-span-1">
        <div className="flex items-center gap-2 text-stone-500">
          <Icon className="w-4 h-4" />
          <div className="label-uppercase !text-[10px]">{label}</div>
        </div>
        <h2 className="font-editorial text-2xl mt-2 leading-tight">{title}</h2>
      </div>
      <div className="md:col-span-2 text-sm text-stone-700 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}


export default function ApplyPrimer() {
  const { country } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/primers/${country}`)
      .then((r) => setP(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [country]);

  if (loading) {
    return <div className="max-w-4xl mx-auto px-6 py-16 text-sm text-stone-500">Loading primer…</div>;
  }

  if (notFound || !p) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="label-uppercase text-stone-500">Not found</div>
        <h1 className="font-editorial text-3xl mt-2">We don't have a primer for that country yet.</h1>
        <Link to="/apply" className="mt-6 inline-flex items-center gap-2 text-stone-800 underline underline-offset-4 decoration-stone-400">
          <ArrowLeft className="w-4 h-4" /> Back to all primers
        </Link>
      </div>
    );
  }

  const t = p.tuition_range_usd;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title={`How to apply to universities in ${p.name} · WhichUniversityAdvisor`}
        description={`${p.one_liner} Application portal: ${p.application_body}. Deadlines, docs, tuition ranges and post-study visa route.`}
        image={`/og/apply-${p.code}.png`}
        path={`/apply/${p.code}`}
      />

      <CountryGuidesLineNote testId="primer-country-guides-line" />
      <Link
        to="/apply"
        data-testid="primer-back-link"
        className="inline-flex items-center gap-2 text-xs text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> All country primers
      </Link>

      <div className="mt-6 flex items-center gap-4">
        <span className="text-6xl leading-none">{p.flag}</span>
        <div>
          <div className="label-uppercase text-stone-500">Applying to universities in</div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-1">{p.name}</h1>
        </div>
      </div>

      <p className="mt-6 text-lg text-stone-700 leading-relaxed max-w-2xl italic" data-testid="primer-oneliner">
        {p.one_liner}
      </p>

      {/* KEY STATS */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="primer-key-stats">
        <div className="border border-stone-200 rounded-lg p-4 bg-white">
          <div className="label-uppercase !text-[9px] text-stone-500">Application portal</div>
          <div className="font-mono text-sm mt-1">{p.application_body}</div>
        </div>
        <div className="border border-stone-200 rounded-lg p-4 bg-white">
          <div className="label-uppercase !text-[9px] text-stone-500">Cycle opens</div>
          <div className="font-mono text-sm mt-1">{p.cycle_opens}</div>
        </div>
        <div className="border border-stone-200 rounded-lg p-4 bg-white">
          <div className="label-uppercase !text-[9px] text-stone-500">Intl tuition</div>
          <div className="font-mono text-sm mt-1">
            {t.low === 0 ? "Free" : fmtUsd(t.low)}–{fmtUsd(t.high)}
          </div>
        </div>
        <div className="border border-stone-200 rounded-lg p-4 bg-white">
          <div className="label-uppercase !text-[9px] text-stone-500">Post-study route</div>
          <div className="font-mono text-sm mt-1">{p.visa_route.name}</div>
        </div>
      </div>

      {/* OUR ANGLE */}
      <div className="mt-10 rounded-lg bg-[#F7F5F0] border border-stone-300 p-6" data-testid="primer-our-angle">
        <div className="flex items-center gap-2 text-stone-600">
          <Sparkles className="w-4 h-4" />
          <div className="label-uppercase !text-[10px]">The WUA angle</div>
        </div>
        <p className="mt-3 text-stone-800 leading-relaxed">{p.our_angle}</p>
      </div>

      {/* DEADLINES */}
      <Section
        icon={Calendar}
        label="Deadlines"
        title="Key dates in the cycle"
        testId="primer-deadlines"
      >
        <ul className="border border-stone-200 rounded-lg bg-white divide-y divide-stone-100">
          {p.key_deadlines.map((d, i) => (
            <li key={i} className="flex items-baseline justify-between px-4 py-3 gap-3">
              <span>{d.label}</span>
              <span className="font-mono text-xs text-stone-500 shrink-0">{d.date}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* REQUIRED DOCS */}
      <Section
        icon={FileText}
        label="Documents"
        title="What you'll need to submit"
        testId="primer-docs"
      >
        <ul className="space-y-1.5">
          {p.required_docs.map((d, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-stone-400 mt-1">•</span>
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* VISA */}
      <Section
        icon={Shield}
        label="Visa & post-study"
        title={p.visa_route.name}
        testId="primer-visa"
      >
        <p>{p.visa_route.psw_summary}</p>
        <a
          href={p.visa_route.gov_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs mt-2 text-stone-800 border border-stone-300 rounded-full px-3 py-1.5 hover:border-stone-500 bg-white"
          data-testid="primer-visa-link"
        >
          Official visa page <ExternalLink className="w-3 h-3" />
        </a>
      </Section>

      {/* COST */}
      <Section
        icon={DollarSign}
        label="Tuition & cost of living"
        title={t.low === 0 ? "Free tuition — with caveats" : `${fmtUsd(t.low)}–${fmtUsd(t.high)} per year`}
        testId="primer-cost"
      >
        <p>{p.cost_notes}</p>
      </Section>

      {/* ENGLISH */}
      <Section
        icon={Languages}
        label="English requirements"
        title="Accepted tests"
        testId="primer-english"
      >
        <div className="flex flex-wrap gap-2">
          {p.english_tests_accepted.map((tst) => (
            <span key={tst} className="text-xs font-mono border border-stone-300 rounded-full px-2.5 py-1 bg-white">
              {tst}
            </span>
          ))}
        </div>
      </Section>

      {/* OUTBOUND LINKS */}
      <div className="mt-12 border-t border-stone-200 pt-8" data-testid="primer-outbound">
        <div className="label-uppercase text-stone-500 mb-3">Official portals</div>
        <div className="flex flex-wrap gap-2">
          {p.outbound.map((o) => (
            <a
              key={o.url}
              href={o.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm border border-stone-300 rounded-full px-4 py-2 hover:border-stone-500 bg-white text-stone-800"
            >
              {o.label} <ExternalLink className="w-3 h-3" />
            </a>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div
        className="mt-16 rounded-lg border border-stone-300 bg-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
        data-testid="primer-cta"
      >
        <div>
          <div className="label-uppercase text-stone-500">Ready to pick a course?</div>
          <h3 className="font-editorial text-2xl mt-1">
            Run Advanced Search for {p.name} courses that fit your grades.
          </h3>
          <p className="text-sm text-stone-600 mt-2 max-w-xl">
            We'll surface courses ranked by outcomes, employer destinations and net-cost — with your grades and passport factored in.
          </p>
        </div>
        <Link
          to="/advanced"
          className="inline-flex items-center gap-2 bg-[#002147] hover:bg-[#001A38] text-white px-5 py-3 rounded-md text-sm"
          data-testid="primer-cta-advanced"
        >
          Open Advanced Search <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <p className="mt-10 text-xs text-stone-500 italic" data-testid="primer-last-reviewed">
        Last reviewed: {p.last_reviewed}. Immigration policy changes fast — please
        verify current rules on the linked government portal before you apply.
      </p>
    </div>
  );
}
